import React, { useState, useEffect, useRef } from 'react';
import { db, handleFirestoreError, OperationType } from '../../lib/firebase';
import { collection, onSnapshot, addDoc, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { InventoryItem } from '../../types';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Plus, Trash2, Edit2, Box, Package, Upload } from 'lucide-react';
import { toast } from 'sonner';
import LCARSConfirmationModal from '../LCARSConfirmationModal';
import Papa from 'papaparse';

export default function InventoryManager() {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<Partial<InventoryItem>>({
    name: '',
    description: '',
    serialNumber: ''
  });

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    description: '',
    onConfirm: () => {},
  });

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'inventory'), (snap) => {
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() } as InventoryItem)));
      setLoading(false);
    }, (err) => {
      handleFirestoreError(err, OperationType.LIST, 'inventory');
    });
    return unsub;
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const dbData = Object.fromEntries(Object.entries(formData).filter(([k, v]) => v !== undefined && k !== 'id'));
      if (editingId) {
        await updateDoc(doc(db, 'inventory', editingId), dbData);
        toast.success('Equipment record updated');
      } else {
        await addDoc(collection(db, 'inventory'), dbData);
        toast.success('Equipment added to manifest');
      }
      setEditingId(null);
      setIsAdding(false);
      setFormData({ name: '', description: '', serialNumber: '' });
    } catch (err) {
      handleFirestoreError(err, OperationType.WRITE, 'inventory');
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          let count = 0;
          for (const row of results.data as any[]) {
            if (!row.name && !row.Name && !row.NAME) continue; // Require at least a name
            
            const newItem: Partial<InventoryItem> = {
              name: row.name || row.Name || row.NAME || 'Unknown Asset',
              description: row.description || row.Description || row.DESCRIPTION || '',
              serialNumber: row.serialNumber || row.serial || row.id || row.SerialNumber || row.SERIALNUMBER || ''
            };
            
            await addDoc(collection(db, 'inventory'), newItem);
            count++;
          }
          toast.success(`Successfully uploaded ${count} assets to the manifest`);
        } catch (error) {
          handleFirestoreError(error, OperationType.WRITE, 'inventory');
          toast.error('Error importing manifest');
        } finally {
          setIsUploading(false);
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
      },
      error: (error) => {
        toast.error(`Error parsing file: ${error.message}`);
        setIsUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    });
  };

  const handleDelete = (id: string) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Decommission Equipment',
      description: 'Remove this physical item from the inventory registry? This cannot be undone.',
      onConfirm: async () => {
        try {
          await deleteDoc(doc(db, 'inventory', id));
          toast.info('Equipment decommissioned');
        } catch (err) {
          handleFirestoreError(err, OperationType.DELETE, 'inventory');
        }
      }
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-lcars-gold/20 p-4 rounded-r-full border-l-8 border-lcars-gold">
        <div>
          <h2 className="text-2xl font-bold text-lcars-gold uppercase">Physical Asset Manifest</h2>
          <p className="text-xs text-[var(--foreground)]/40 font-bold tracking-widest uppercase">Inventory Registry & Serial Tracking</p>
        </div>
        <div className="flex gap-2">
          <input 
            type="file" 
            accept=".csv" 
            className="hidden" 
            ref={fileInputRef}
            onChange={handleFileUpload}
          />
          <button 
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className={`lcars-pill bg-white/10 text-[var(--foreground)] flex items-center gap-2 px-6 py-2 text-sm font-bold transition-all ${isUploading ? 'opacity-50' : 'hover:bg-white/20'}`}
            title="Upload CSV Manifest (Headers: name, description, serialNumber)"
          >
            <Upload className="w-4 h-4" /> {isUploading ? 'UPLOADING...' : 'UPLOAD MANIFEST'}
          </button>
          <button 
            onClick={() => { setIsAdding(true); setEditingId(null); }}
            className="lcars-pill bg-lcars-blue text-black flex items-center gap-2 px-6 py-2 text-sm font-bold"
          >
            <Plus className="w-4 h-4" /> ADD ASSET
          </button>
        </div>
      </div>

      {(isAdding || editingId) && (
        <form onSubmit={handleSubmit} className="bg-white/5 border border-lcars-gold p-6 rounded-r-full space-y-4 animate-in slide-in-from-top duration-300">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <label htmlFor="inv-name" className="text-[0.6rem] text-lcars-gold font-bold uppercase ml-4">Asset Name</label>
              <Input 
                id="inv-name"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                required
                className="bg-[var(--background)] border-lcars-gold text-[var(--foreground)]"
                placeholder="e.g., Captain's Chair"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="inv-serial" className="text-[0.6rem] text-lcars-gold font-bold uppercase ml-4">Serial Number / ID</label>
              <Input 
                id="inv-serial"
                value={formData.serialNumber}
                onChange={e => setFormData({ ...formData, serialNumber: e.target.value })}
                className="bg-[var(--background)] border-lcars-gold text-[var(--foreground)]"
                placeholder="PRO-001"
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="inv-desc" className="text-[0.6rem] text-lcars-gold font-bold uppercase ml-4">Description</label>
              <Input 
                id="inv-desc"
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                className="bg-[var(--background)] border-lcars-gold text-[var(--foreground)]"
                placeholder="Physical dimensions, status, etc."
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setIsAdding(false); setEditingId(null); }} className="lcars-pill bg-lcars-red text-[var(--foreground)] px-4 py-2 text-xs font-bold uppercase">Abort</button>
            <button type="submit" className="lcars-pill bg-lcars-blue text-black px-4 py-2 text-xs font-bold uppercase">Log to Core</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(item => (
          <div key={item.id} className="bg-[var(--background)] border-l-8 border-lcars-blue p-4 hover:bg-white/5 transition-all group relative">
            <div className="flex justify-between items-start mb-2">
              <span className="text-[0.6rem] font-bold text-lcars-blue uppercase tracking-widest">{item.serialNumber || 'UNSERIALIZED'}</span>
              <div className="flex gap-1 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                <button onClick={() => { setEditingId(item.id!); setFormData(item); }} className="p-1 hover:text-lcars-blue text-[var(--foreground)]/50"><Edit2 className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(item.id!)} className="p-1 hover:text-lcars-red text-[var(--foreground)]/50"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <h3 className="text-lg font-bold text-[var(--foreground)] mb-1 flex items-center gap-2">
              <Package className="w-4 h-4 text-lcars-gold" />
              {item.name}
            </h3>
            <p className="text-xs text-[var(--foreground)]/60 line-clamp-2">{item.description || 'No description recorded.'}</p>
          </div>
        ))}
        {items.length === 0 && !loading && (
          <div className="col-span-full py-12 text-center bg-white/5 border-2 border-dashed border-white/10 rounded-lg">
             <Box className="w-12 h-12 mx-auto mb-2 text-[var(--foreground)]/20" />
             <p className="text-[var(--foreground)]/40 font-bold uppercase tracking-widest">Inventory empty. No registered assets.</p>
          </div>
        )}
      </div>

      <LCARSConfirmationModal
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
      />
    </div>
  );
}
